import React, {
  createContext,
  FC,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import { Preferences } from '@capacitor/preferences';
import { useLocation, useNavigate } from 'react-router-dom';
import useBreadcrumbs, {
  BreadcrumbData,
  BreadcrumbsRoute,
} from 'use-react-router-breadcrumbs';
import { unitBreadcrumbRoutes } from '../../routes/units-routes';
import { propertyBreadcrumbRoutes } from '../../routes/property-routes';
import { appRoutes } from '../../routes/app-routes';
import { useAuthenticator } from '../../auth/AuthenticationContext';
import {
  AUTH_ID_TOKEN_NAME,
  AUTH_REDIRECT_URL_KEY,
} from '../../../helpers/config';
import { LocalState } from '../../system/services/localStateManager';
import { humanize } from '../shell/utilities';
import { IServerToken } from '../../auth/models';
import {
  buildSystemHistoryStorageKey,
  loadHistoryStorageState,
  normalizeHistoryStorageState,
  removeHistoryStorageState,
  saveHistoryStorageState,
} from './history-storage';

type HistoryEntry = [string, React.ReactNode];
type SerializableHistoryEntry = [string, string];

// Converts HistoryEntry tuples (which may contain React elements) to plain
// strings that can be round-tripped through JSON.
function serializeHistory(entries: HistoryEntry[]): SerializableHistoryEntry[] {
  return entries.map(([path, breadcrumb]) => {
    const label = React.isValidElement(breadcrumb)
      ? (breadcrumb.props.children?.toString() ?? '')
      : String(breadcrumb ?? '');
    return [path, label];
  });
}

function deserializeHistory(
  entries: SerializableHistoryEntry[],
): HistoryEntry[] {
  return entries.map(([path, label]) => [path, label]);
}

export interface ISystemHistoryManager {
  forward(): Promise<void>;
  back(): Promise<void>;
  go(newIndex: number): Promise<void>;
  clear(): Promise<void>;
  hasNext: boolean;
  hasPrevious: boolean;
  hasLast: boolean;
  last(): void;
  currentLabel: string;
  setCurrentLabel(label: string): Promise<void>;
  stack: HistoryEntry[];
  currentIndex: number;
}

interface SystemHistoryProviderProps {
  children: React.ReactNode;
}

const GlobalIgnorePathPatterns = [
  /^\/$/, // Exact string '/'
  /login/, // Contains 'login'
  /^\/auth/, // Any path that begins with '/auth'
  /auth\/passwords\/reset/, // Contains 'auth/passwords/reset'
  /logging-out/, // Contains 'logging-out'
  /^\/users\/[0-9,a-b,A-B]+/,
  /^\/users\/[0-9,a-b,A-B]+\/$/,
  /^\/users\/[0-9,a-b,A-B]+\/tab$/,
  /^\/properties\/[^/]+\/edit-property$/, // Property edit URLs
];

const defaultValue: ISystemHistoryManager = {
  forward: async () => {},
  back: async () => {},
  clear: async () => {},
  go: async () => {},
  hasNext: false,
  hasPrevious: false,
  hasLast: false,
  last: () => {},
  currentLabel: '',
  setCurrentLabel: async () => {},
  stack: [],
  currentIndex: 0,
};

const SystemHistoryContext = createContext<ISystemHistoryManager>(defaultValue);

SystemHistoryContext.displayName = 'SystemHistoryContext';

export const SystemHistoryProvider: FC<SystemHistoryProviderProps> = ({
  children,
}) => {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const { user } = useAuthenticator();
  const currentFullPath = `${pathname}${search ?? ''}`;
  const currentFullPathRef = useRef(currentFullPath);
  currentFullPathRef.current = currentFullPath;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const endpointToken = LocalState.getItem<IServerToken>(AUTH_ID_TOKEN_NAME);
  const endpointTokenValue = endpointToken?.value;
  const historyStorageKey = useMemo(
    () => buildSystemHistoryStorageKey(user?._id, endpointTokenValue),
    [user?._id, endpointTokenValue],
  );

  const [lastPath, setLastPath] = useState<string>('');
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [movingForward, setMovingForward] = useState<boolean>(false);
  const [movingBackward, setMovingBackward] = useState<boolean>(false);
  const [repositioning, setRepositioning] = useState<boolean>(false);
  const [initialized, setInitialized] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);

  useEffect(() => {
    const targetUrl = LocalState.getItem<string>(AUTH_REDIRECT_URL_KEY);
    setRedirectUrl(targetUrl ?? null);
  }, [setRedirectUrl]);

  const myRoutes = useMemo(
    () => [...propertyBreadcrumbRoutes, ...unitBreadcrumbRoutes, ...appRoutes],
    [],
  );

  const breadcrumbs = useBreadcrumbs(myRoutes);

  const updateHistory = useCallback(
    async (newHistory: HistoryEntry[], newIndex: number) => {
      if (!initialized) return;
      const normalizedState = normalizeHistoryStorageState({
        history: serializeHistory(newHistory),
        currentIndex: newIndex,
      });
      const normalizedHistory = deserializeHistory(normalizedState.history);

      setHistory(normalizedHistory);
      setCurrentIndex(normalizedState.currentIndex);

      if (historyStorageKey) {
        saveHistoryStorageState(historyStorageKey, normalizedState).catch(
          (error) => {
            console.error('Failed to save navigation history locally', error);
          },
        );
      }
    },
    [historyStorageKey, initialized],
  );

  /**
   * Initialization Effect
   *
   * Loads persisted history on mount from the local user + endpoint scoped
   * Preferences record. Server-side UserHistory behavior is preserved for
   * older clients, but current clients keep this UI state local.
   */
  useEffect(() => {
    let cancelled = false;
    const initializationStartPath = currentFullPathRef.current;

    const initialize = async () => {
      setInitialized(false);

      if (!historyStorageKey) {
        if (cancelled) return;
        setHistory([]);
        setCurrentIndex(0);
        setLastPath('');
        setInitialized(true);
        return;
      }

      try {
        const storedHistory = await loadHistoryStorageState(historyStorageKey);
        if (cancelled) return;

        const loadedHistory = deserializeHistory(storedHistory?.history ?? []);
        let newIndex = storedHistory?.currentIndex ?? 0;
        let destination = loadedHistory[newIndex]?.[0];
        const routeChangedDuringInitialization =
          currentFullPathRef.current !== initializationStartPath;
        const restoringStoredDestination =
          !!destination && !redirectUrl && !routeChangedDuringInitialization;

        if (redirectUrl) {
          destination = redirectUrl;
          newIndex = loadedHistory.length ? loadedHistory.length - 1 : 0;
        }

        setHistory(loadedHistory);
        setCurrentIndex(newIndex);
        setLastPath(restoringStoredDestination ? destination : '');
        setInitialized(true);

        if (destination && (redirectUrl || restoringStoredDestination)) {
          navigateRef.current(destination);
        }

        if (redirectUrl) {
          LocalState.removeItem(AUTH_REDIRECT_URL_KEY);
          await Preferences.remove({ key: AUTH_REDIRECT_URL_KEY });
        }
      } catch (error) {
        if (cancelled) return;
        console.error(
          '[SystemHistoryProvider] local history initialization failed.',
          error,
        );
        setHistory([]);
        setCurrentIndex(0);
        setLastPath('');
        setInitialized(true);
      }
    };

    initialize();

    return () => {
      cancelled = true;
    };
  }, [historyStorageKey, redirectUrl]);

  type AugmentedBreadcrumbsRoute = BreadcrumbsRoute<string> & {
    state: {
      ignore?: RegExp[];
      to: string;
    };
  };

  // Async handler for history updates
  const handleHistoryUpdate = useCallback(
    async (
      shortPath: string,
      fullPath: string,
      firstMatch: BreadcrumbData<string> | undefined,
    ) => {
      if (!firstMatch) return;

      const pathName = humanize(shortPath);

      if (history.length === 0) {
        await updateHistory([[fullPath, pathName]], 0);
      } else if (history[currentIndex][0] !== fullPath) {
        if (
          currentIndex < history.length - 1 &&
          history[currentIndex + 1][0] === fullPath
        ) {
          await updateHistory(history, currentIndex + 1);
        } else {
          const newArray = [...history];
          newArray.splice(currentIndex + 1, 0, [fullPath, pathName]);
          await updateHistory(newArray, currentIndex + 1);
        }
      }
    },
    [currentIndex, history, updateHistory],
  );

  /**
   * Navigation Tracking Effect
   *
   * Purpose: Tracks and updates the history state based on route changes.
   *
   * Importance:
   * - Maintains an accurate navigation history as users move through the app
   * - Handles special routing cases (ignored routes, redirects)
   * - Updates the history stack with new entries when navigating
   * - Preserves the ability to navigate back/forward through history
   *
   * Dependencies: All navigation-related state and functions
   * - pathname, state, search: Current route information
   * - breadcrumbs: Current breadcrumb state
   * - currentIndex, history: Current history state
   * - myRoutes: Available routes configuration
   * - navigate: Navigation function
   * - updateHistory: History update function
   */
  useEffect(() => {
    if (GlobalIgnorePathPatterns.find((pattern) => pattern.test(pathname))) {
      return;
    }
    if (movingBackward || movingForward || repositioning) return;
    if (!initialized) return;

    const fullPath = `${pathname}${search ?? ''}`;

    if (lastPath === fullPath) return;

    setTimeout(() => {
      setLastPath(fullPath);

      const firstMatch = breadcrumbs
        .slice()
        .reverse()
        .find((x) => {
          if (
            pathname !== x.match.pathname &&
            pathname !== `${x.match.pathname}/`
          ) {
            return false;
          }

          const myRoute: AugmentedBreadcrumbsRoute = x.match
            .route as unknown as AugmentedBreadcrumbsRoute;
          return !(
            myRoute?.state?.ignore &&
            myRoute.state.ignore.find((spec: RegExp) => spec.test(fullPath))
          );
        });

      const firstRoute = firstMatch?.match.route as AugmentedBreadcrumbsRoute;

      if (
        firstRoute?.state?.ignore &&
        firstRoute.state.ignore.find((spec: RegExp) => spec.test(fullPath))
      ) {
        return;
      } else if (firstRoute?.state?.to) {
        navigate(firstRoute.state.to);
      } else if (pathname) {
        if (firstRoute) {
          if (
            !GlobalIgnorePathPatterns.find((pattern) => pattern.test(fullPath))
          ) {
            if (firstMatch) {
              // Call the async function
              setTimeout(() => {
                handleHistoryUpdate(pathname, fullPath, firstMatch).catch(
                  (error) => {
                    console.error('Error updating history:', error);
                  },
                );
              });
            }
          }
        }
      }
    });
  }, [
    movingForward,
    movingBackward,
    repositioning,
    pathname,
    search,
    breadcrumbs,
    currentIndex,
    history,
    lastPath,
    myRoutes,
    handleHistoryUpdate,
    initialized,
    navigate,
  ]);

  const value = useMemo<ISystemHistoryManager>(() => {
    return {
      forward: async () => {
        setMovingForward(true);
        setTimeout(async () => {
          if (currentIndex < history.length - 1) {
            const newIndex = currentIndex + 1;
            const destinationPath = history[newIndex][0];
            await updateHistory(history, newIndex);
            navigate(destinationPath);
          }
          setTimeout(() => setMovingForward(false));
        });
      },
      back: async () => {
        setMovingBackward(true);
        setTimeout(async () => {
          if (currentIndex > 0) {
            const newIndex = currentIndex - 1;
            const destinationPath = history[newIndex][0];
            await updateHistory(history, newIndex);
            navigate(destinationPath);
          }
          setTimeout(() => setMovingBackward(false));
        });
      },
      clear: async () => {
        setHistory([]);
        setCurrentIndex(0);
        setLastPath('');

        if (historyStorageKey) {
          await removeHistoryStorageState(historyStorageKey);
        }
      },
      go: async (newIndex: number) => {
        if (newIndex < (history?.length ?? 0)) {
          setRepositioning(true);
          setTimeout(() => {
            setCurrentIndex(newIndex);
            setTimeout(async () => {
              await updateHistory(history, newIndex);
              navigate(history[newIndex][0]);
              setTimeout(() => setRepositioning(false));
            });
          });
        }
      },
      hasNext: currentIndex < history.length - 1,
      hasPrevious: currentIndex > 0 && !movingForward && !movingBackward,
      hasLast: history.length > 0 && !movingForward && !movingBackward,
      last: () => {
        if (history.length > 0) {
          const lastIndex = history.length - 1;
          setCurrentIndex(lastIndex);
          void updateHistory(history, lastIndex);
          navigate(history[lastIndex][0], { replace: true });
        }
      },
      setCurrentLabel: async (label: string) => {
        if (repositioning || movingBackward || movingForward) return;

        if (history.length > 0 && !movingForward && !movingBackward) {
          const lastIndex = currentIndex;
          if (history[lastIndex] && history[lastIndex][1] !== label) {
            const newHistory = [...history];
            newHistory[lastIndex] = [history[lastIndex][0], label];
            await updateHistory(newHistory, lastIndex);
          }
        }
      },
      currentLabel:
        typeof (history[history.length - 1] ?? ['', null])[1] === 'string'
          ? (history[history.length - 1][1] as string)
          : '',
      stack: history,
      currentIndex,
    };
  }, [
    repositioning,
    history,
    currentIndex,
    navigate,
    updateHistory,
    movingBackward,
    movingForward,
    historyStorageKey,
  ]);

  return (
    <SystemHistoryContext.Provider value={value}>
      {children}
    </SystemHistoryContext.Provider>
  );
};

export function useSystemHistory(): ISystemHistoryManager {
  const context = useContext(SystemHistoryContext);
  if (!context) {
    throw new Error(
      'useSystemHistory must be used within a SystemHistoryProvider',
    );
  }
  return context;
}
